import React, { useState, KeyboardEvent, ChangeEvent } from "react";
import { useField, useFormikContext } from "formik";
import { Form, Badge } from "react-bootstrap";
import useTagInputStyles from "./styles";

interface TagInputProps {
    name: string;
    placeholder?: string;
    maxTags?: number;
    helperText?: string;
    itemLabel?: string;
}

const normalize = (tag: string) => tag.trim().toLowerCase();

const TagInput: React.FC<TagInputProps> = (props) => {

    const tagInputStyles = useTagInputStyles();

    const placeholder = props.placeholder ?? "Add tag...";
    const helperText = props.helperText ?? "Press Enter or Return to add tag";
    const itemLabel = props.itemLabel ?? "Tag";

    const [input, setInput] = useState<string>("");

    const [field, meta] = useField<string[]>(props.name);
    const { setFieldValue, submitCount } = useFormikContext<any>();

    const value: string[] = field.value || [];

    const isDuplicate =
        input.trim() !== "" &&
        value.some((tag) => normalize(tag) === normalize(input));

    const showError = meta.touched || submitCount > 0;
    const hasError = (showError && !!meta.error) || isDuplicate;

    const addTag = (tag: string) => {
        const trimmed = tag.trim();
        const normalized = normalize(tag);

        const exists = value.some((t) => normalize(t) === normalized);

        if (!exists && (!props.maxTags || value.length < props.maxTags)) {
            setFieldValue(props.name, [...value, trimmed]);
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && input.trim() !== "") {
            e.preventDefault();
            addTag(input);
            setInput("");
        }

        if (e.key === "Backspace" && input === "" && value.length > 0) {
            setFieldValue(props.name, value.slice(0, -1));
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const removeTag = (tagToRemove: string) => {
        setFieldValue(
            props.name,
            value.filter((tag) => tag !== tagToRemove)
        );
    };

    return (
        <Form.Group>
            <div className={`border rounded p-2 ${hasError ? "border-danger" : ""}`}>
                {/* Tags */}
                <div className="d-flex flex-wrap gap-2 mb-1">
                    {value.map((tag, index) => (
                        <Badge
                            bg="light"
                            key={index}
                            className="d-flex align-items-center"
                            style={{ ...tagInputStyles.badge }}
                        >
                            {tag}
                            <span
                                style={{ ...tagInputStyles.close }}
                                onClick={() => removeTag(tag)}
                            >
                                ×
                            </span>
                        </Badge>
                    ))}
                </div>

                {/* Input */}
                <Form.Control
                    type="text"
                    name={props.name}
                    value={input}
                    placeholder={placeholder}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    isInvalid={hasError}
                    className="border-0 shadow-none"
                />
            </div>

            {/* Error message */}
            {hasError ? (
                <Form.Control.Feedback type="invalid" className="d-block mb-2">
                    {isDuplicate ? `${itemLabel} already exists` : meta.error}
                </Form.Control.Feedback>
            ) : (
                helperText && (
                    <div
                        className="text-muted mt-1 text-end"
                        style={{ fontSize: "0.8rem" }}
                    >
                        {helperText}
                    </div>
                )
            )}
        </Form.Group>
    );
};

export default TagInput;